/**
 * The Grid's push subscription (#1465, step 2).
 *
 * The server sends one `dispatch:changed` event per org per ~400 ms whenever a
 * row the board draws changes anywhere — another dispatcher's drop, a tech's
 * status from the truck, the worker's auto-scheduler (migration 587 +
 * services/liveChanges.ts). The event is a hint, not data: on receipt the
 * board re-reads the queries it is showing. The #1464 polling stays underneath
 * as the floor for the minutes the stream is reconnecting.
 *
 * One place for the key list so the Grid, the Map and the Route planner
 * invalidate the same things and nobody re-types it.
 */
import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSseInvalidate, useSseStatus, type SseStatus } from "@/lib/use-sse";

export const DISPATCH_CHANGED_EVENT = "dispatch:changed";

/** Query-key prefixes the board draws from. */
export const DISPATCH_LIVE_KEYS: readonly (readonly string[])[] = [
    ["dispatch"],
    ["dispatch-tech-routes"],
    ["dispatch-map-jobs"],
    ["dispatch-map"],
    ["work-pulse"],
    ["dashboard", "missed-jobs"],
];

export interface DispatchChanged {
    count: number;
    tables: Record<string, number>;
    ids: string[];
    at: string;
}

export interface LiveDispatch {
    status: SseStatus;
    /** Server time of the last change hint we acted on, or null. */
    lastChangeAt: Date | null;
    /** How many rows that hint covered. */
    lastChangeCount: number;
    /** Per-table counts from that hint, for the chip's hover. */
    lastChangeTables: Record<string, number> | undefined;
}

/**
 * Keep the board current. Call once per board-shaped screen. Returns the
 * stream's health and the last hint, for the live indicator.
 */
export function useLiveDispatch(enabled = true): LiveDispatch {
    const qc = useQueryClient();
    const status = useSseStatus();
    const [last, setLast] = useState<{ at: Date; count: number; tables?: Record<string, number> } | null>(null);
    const invalidating = useRef(false);

    const onChange = useCallback(
        (data: unknown) => {
            const d = (data ?? {}) as Partial<DispatchChanged>;
            const at = typeof d.at === "string" ? new Date(d.at) : new Date();
            setLast({
                at,
                count: typeof d.count === "number" ? d.count : 1,
                tables: d.tables && typeof d.tables === "object" ? d.tables : undefined,
            });
            // Two hints inside one tick invalidate once.
            if (invalidating.current) return;
            invalidating.current = true;
            queueMicrotask(() => {
                invalidating.current = false;
                for (const key of DISPATCH_LIVE_KEYS) void qc.invalidateQueries({ queryKey: [...key] });
            });
        },
        [qc],
    );
    useSseInvalidate(DISPATCH_CHANGED_EVENT, onChange, enabled);

    return {
        status,
        lastChangeAt: last?.at ?? null,
        lastChangeCount: last?.count ?? 0,
        lastChangeTables: last?.tables,
    };
}
